import { useState } from "react";

import { Button, Modal } from "@synthesis/shared-ui";
import { useNavigate } from "@tanstack/react-router";
import { IconArrowLeft, IconFileDelete } from "@synthesis/shared-icons";
import { cn } from "@synthesis/shared-utils";
import { observer } from "mobx-react-lite";

import { usePageStore } from "../create-application-page.lib";
import { useSaveApplicationDraft } from "../hooks/use-save-create-application-draft";
import { useDeleteApplicationDraft } from "../hooks/use-delete-application-draft";
import { useApplicationDraftId } from "../hooks/use-create-application-draft-id";

type GoBackButtonProps = {
  className?: string;
};

export const GoBackButton = observer(({ className }: GoBackButtonProps) => {
  const navigate = useNavigate();
  const pageStore = usePageStore();
  const { saveDraftAndLeave, isSaving } = useSaveApplicationDraft();
  const { deleteDraft, isDeleting } = useDeleteApplicationDraft();
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { draftId } = useApplicationDraftId();

  const handleBackClick = () => {
    if (!pageStore?.selectedCategory || draftId) {
      navigate({ to: "/" });
      return;
    }

    setIsLeaveModalOpen(true);
  };

  const handleLeaveWithoutSaving = () => {
    setIsLeaveModalOpen(false);
    navigate({ to: "/" });
  };

  const handleSaveAndLeave = () => {
    setIsLeaveModalOpen(false);
    saveDraftAndLeave();
  };

  const handleLeaveModalOpenChange = (open: boolean) => {
    if (!isSaving) {
      setIsLeaveModalOpen(open);
    }
  };

  const handleDeleteModalOpenChange = (open: boolean) => {
    if (!isDeleting) {
      setIsDeleteModalOpen(open);
    }
  };

  const canDeleteDraft = Boolean(draftId) && pageStore?.editingMode !== "revision";

  const handleDeleteDraft = () => {
    if (!draftId || isDeleting) {
      return;
    }

    setIsDeleteModalOpen(false);
    deleteDraft(draftId);
  };

  return (
    <>
      <div className={cn("flex w-full items-center justify-between", className)}>
        <Button
          type="button"
          theme="text"
          size="sm"
          disabled={isSaving || isDeleting}
          onClick={handleBackClick}
          leftIcon={<IconArrowLeft className="size-5" />}
        >
          {draftId ? "Выйти" : "Назад"}
        </Button>

        {canDeleteDraft && (
          <Button
            type="button"
            theme="secondary"
            size="sm"
            disabled={isSaving || isDeleting}
            onClick={() => setIsDeleteModalOpen(true)}
            leftIcon={<IconFileDelete className="size-5" />}
          >
            Удалить черновик
          </Button>
        )}
      </div>

      <Modal.Root isOpen={isLeaveModalOpen} setIsOpen={handleLeaveModalOpenChange}>
        <Modal.Content className="w-[32rem]">
          <Modal.Title>Сохранить черновик заявки?</Modal.Title>
          <div className="flex flex-col gap-4 p-8 pb-6">
            <p className="paragraph-base text-type-common">Черновик заявки будет сохранен и вы сможете его продолжить позже.</p>
            <div className="flex gap-4 pt-2">
              <Button type="button" theme="primary" size="lg" disabled={isSaving} onClick={handleSaveAndLeave}>
                Сохранить
              </Button>
              <Button type="button" theme="secondary" size="lg" disabled={isSaving} onClick={handleLeaveWithoutSaving}>
                Выйти без сохранения
              </Button>
            </div>
          </div>
        </Modal.Content>
      </Modal.Root>

      <Modal.Root isOpen={isDeleteModalOpen} setIsOpen={handleDeleteModalOpenChange}>
        <Modal.Content className="w-[40rem]">
          <Modal.Title>Удаление черновика заявки</Modal.Title>
          <div className="flex flex-col gap-4 px-8 pb-2 pt-6">
            <h3 className="heading-h4 text-type-common">Безвозвратное удаление черновика заявки</h3>
            <p className="paragraph-base text-type-minor">Все заполненные данные будут удалены. Вы сможете создать новую заявку.</p>
          </div>
          <div className="flex shrink-0 gap-4 px-8 pb-6 pt-8">
            <Button type="button" theme="primary" size="lg" disabled={isDeleting} onClick={handleDeleteDraft}>
              Удалить
            </Button>
            <Button type="button" theme="secondary" size="lg" disabled={isDeleting} onClick={() => setIsDeleteModalOpen(false)}>
              Отмена
            </Button>
          </div>
        </Modal.Content>
      </Modal.Root>
    </>
  );
});
